import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { type ApiKeyCreated, apiKeysApi } from '@/api/api-keys.api';
import { formatApiMessage } from '@/api/client';
import { type WebhookCreated, webhooksApi } from '@/api/webhooks.api';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { formatDateTime } from '@/lib/format';

export function AdminIntegrationsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'api' | 'webhooks'>('api');
  return (
    <AdminLayout title={t('admin.integrations_title')} subtitle={t('admin.integrations_subtitle')}>
      <nav className="tabs mb-4">
        <button className={`tab ${tab === 'api' ? 'tab-active' : ''}`} onClick={() => setTab('api')}>
          {t('admin.api_keys')}
        </button>
        <button className={`tab ${tab === 'webhooks' ? 'tab-active' : ''}`} onClick={() => setTab('webhooks')}>
          {t('admin.webhooks')}
        </button>
      </nav>
      {tab === 'api' ? <ApiKeysTab /> : <WebhooksTab />}
    </AdminLayout>
  );
}

function ApiKeysTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [newKey, setNewKey] = useState<ApiKeyCreated | null>(null);

  const { data: keys = [] } = useQuery({
    queryKey: ['api-keys', 'list'],
    queryFn: () => apiKeysApi.list(),
  });

  const create = useMutation({
    mutationFn: () => apiKeysApi.create({ name: name.trim() || 'Untitled' }),
    onSuccess: (k) => {
      setNewKey(k);
      setName('');
      qc.invalidateQueries({ queryKey: ['api-keys', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (uuid: string) => apiKeysApi.revoke(uuid),
    onSuccess: () => {
      toast.success(t('admin.key_revoked'));
      qc.invalidateQueries({ queryKey: ['api-keys', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div>
      <div className="card mb-4">
        <h3 className="text-base font-semibold text-gray-900 mb-2">{t('admin.new_api_key')}</h3>
        <p className="text-[12px] text-muted mb-3">
          <code>Authorization: Bearer esd_…</code> {t('admin.api_keys_help')}
        </p>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder={t('admin.key_name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button onClick={() => create.mutate()} disabled={create.isPending} className="btn btn-primary">
            {create.isPending ? t('common.creating') : t('admin.create_key')}
          </button>
        </div>

        {newKey && (
          <div className="mt-4 bg-success-bg border border-success rounded-md p-3 text-[13px]">
            <div className="font-semibold mb-1">{t('admin.key_show_once')}</div>
            <code className="bg-white border border-border rounded p-2 block break-all text-[12px] my-2">
              {newKey.rawToken}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(newKey.rawToken);
                toast.success(t('common.copied'));
              }}
              className="btn btn-secondary btn-sm"
            >
              {t('common.copy')}
            </button>
            <button onClick={() => setNewKey(null)} className="btn btn-ghost btn-sm ml-2">{t('common.close')}</button>
          </div>
        )}
      </div>

      <div className="card !p-0">
        {keys.length === 0 ? (
          <div className="text-center py-8 text-muted text-[13px]">{t('admin.no_keys')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('admin.prefix')}</th>
                <th>{t('common.created')}</th>
                <th>{t('admin.last_used')}</th>
                <th>{t('admin.status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className={k.revoked ? 'opacity-50' : ''}>
                  <td className="font-medium text-gray-900">{k.name}</td>
                  <td><code className="text-[12px]">{k.prefix}…</code></td>
                  <td className="text-[12px] text-muted">{formatDateTime(k.createdAt)}</td>
                  <td className="text-[12px] text-muted">{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : t('admin.never')}</td>
                  <td>
                    <span className={`badge ${k.revoked ? 'badge-danger' : 'badge-success'} text-[10px]`}>
                      {k.revoked ? t('admin.revoked') : t('admin.active')}
                    </span>
                  </td>
                  <td className="text-right">
                    {!k.revoked && (
                      <button
                        onClick={() => {
                          if (confirm(t('admin.revoke_confirm', { name: k.name }))) revoke.mutate(k.id);
                        }}
                        className="btn btn-ghost btn-sm text-danger"
                      >{t('admin.revoke')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function WebhooksTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [newHook, setNewHook] = useState<WebhookCreated | null>(null);

  const { data: hooks = [] } = useQuery({
    queryKey: ['webhooks', 'list'],
    queryFn: () => webhooksApi.list(),
  });
  const { data: availableEvents = [] } = useQuery({
    queryKey: ['webhooks', 'events'],
    queryFn: () => webhooksApi.listEvents(),
  });

  const create = useMutation({
    mutationFn: () => webhooksApi.create({ name: name.trim() || 'Untitled', url, events }),
    onSuccess: (h) => {
      setNewHook(h);
      setName('');
      setUrl('');
      setEvents([]);
      qc.invalidateQueries({ queryKey: ['webhooks', 'list'] });
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (uuid: string) => webhooksApi.delete(uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks', 'list'] }),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div>
      <div className="card mb-4">
        <h3 className="text-base font-semibold text-gray-900 mb-2">{t('admin.new_webhook')}</h3>
        <p className="text-[12px] text-muted mb-3">{t('admin.webhooks_help')}</p>
        <div className="space-y-2">
          <input className="input" placeholder={t('common.name')} value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" placeholder="https://example.com/hook" value={url} onChange={(e) => setUrl(e.target.value)} />
          <div>
            <div className="text-[12px] font-semibold mb-1.5">{t('admin.events')}</div>
            <div className="flex flex-wrap gap-1.5">
              {availableEvents.map((ev) => {
                const on = events.includes(ev);
                return (
                  <button
                    key={ev}
                    type="button"
                    onClick={() => setEvents((p) => (on ? p.filter((x) => x !== ev) : [...p, ev]))}
                    className={`text-[11px] px-2 py-1 rounded border ${on ? 'bg-primary text-white border-primary' : 'bg-white border-border text-gray-700'}`}
                  >
                    {ev}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button
              onClick={() => create.mutate()}
              disabled={create.isPending || !url || events.length === 0}
              className="btn btn-primary"
            >
              {create.isPending ? t('common.creating') : t('admin.add_webhook')}
            </button>
          </div>
        </div>

        {newHook && (
          <div className="mt-4 bg-success-bg border border-success rounded-md p-3 text-[13px]">
            <div className="font-semibold mb-1">{t('admin.secret_show_once')}</div>
            <code className="bg-white border border-border rounded p-2 block break-all text-[12px] my-2">
              {newHook.secret}
            </code>
            <button onClick={() => { navigator.clipboard.writeText(newHook.secret); toast.success(t('common.copied')); }} className="btn btn-secondary btn-sm">{t('common.copy')}</button>
            <button onClick={() => setNewHook(null)} className="btn btn-ghost btn-sm ml-2">{t('common.close')}</button>
          </div>
        )}
      </div>

      <div className="card !p-0">
        {hooks.length === 0 ? (
          <div className="text-center py-8 text-muted text-[13px]">{t('admin.no_webhooks')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>URL</th>
                <th>{t('admin.events')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {hooks.map((h) => (
                <tr key={h.id}>
                  <td className="font-medium">{h.name}</td>
                  <td><code className="text-[11px]">{h.url}</code></td>
                  <td><span className="text-[11px] text-muted">{h.events.length} events</span></td>
                  <td className="text-right">
                    <button
                      onClick={() => {
                        if (confirm(t('admin.delete_webhook_confirm'))) remove.mutate(h.id);
                      }}
                      className="btn btn-ghost btn-sm text-danger"
                    >{t('common.delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
